import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, TrendingUp, Calculator, Building, Briefcase, Globe, Star, CheckCircle, ChevronLeft, ChevronRight, Menu, X, PieChart } from 'lucide-react';
import './Option2.css';
import './components/FinancialStrategy.css';
import './components/SimulationPatrimoine.css';

interface Option2Props {
  onNavigateBack: () => void;
  onStartSimulator: () => void;
  onStartInscription: () => void;
  onOpenMonCompte: () => void;
  onStartChat: () => void;
  onStartProfilRisque: () => void;
  onStartSimulateurProduits: () => void;
}

const Option2: React.FC<Option2Props> = ({
  onNavigateBack,
  onStartSimulator,
  onStartInscription, // eslint-disable-line @typescript-eslint/no-unused-vars
  onOpenMonCompte,
  onStartChat,
  onStartProfilRisque, // eslint-disable-line @typescript-eslint/no-unused-vars
  onStartSimulateurProduits
}) => {
  // État pour le carousel d'images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // État pour le menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Types pour les profils d'investisseurs
  type ProfilInvestisseur = 'Prudent' | 'Équilibré' | 'Dynamique';
  
  // États pour le simulateur interactif
  const [simulatorData, setSimulatorData] = useState<{
    objectif: number;
    horizon: number;
    capitalInitial: number;
    versementMensuel: number;
    profilInvestisseur: ProfilInvestisseur;
  }>({
    objectif: 100000,
    horizon: 10,
    capitalInitial: 10000,
    versementMensuel: 300,
    profilInvestisseur: 'Équilibré'
  });
  
  // Profils d'investisseurs avec taux de rendement
  const profilsInvestisseurs: Record<ProfilInvestisseur, { taux: number; description: string; color: string }> = {
    'Prudent': { taux: 0.04, description: 'Sécurité maximale', color: 'from-blue-500 to-blue-600' },
    'Équilibré': { taux: 0.06, description: 'Rendement modéré', color: 'from-emerald-500 to-emerald-600' },
    'Dynamique': { taux: 0.08, description: 'Croissance élevée', color: 'from-purple-500 to-purple-600' }
  };

  // Calcul des performances en temps réel
  const calculatePerformance = () => {
    const { objectif, horizon, capitalInitial, versementMensuel, profilInvestisseur } = simulatorData;
    const totalVersements = versementMensuel * 12 * horizon;
    const capitalTotal = capitalInitial + totalVersements;
    const rendementAnnuel = profilsInvestisseurs[profilInvestisseur].taux;
    
    // Calcul avec intérêts composés
    const valeurFinale = capitalInitial * Math.pow(1 + rendementAnnuel, horizon) + 
                        versementMensuel * ((Math.pow(1 + rendementAnnuel/12, 12 * horizon) - 1) / (rendementAnnuel/12)) * 12;
    
    const gain = valeurFinale - capitalTotal;
    const pourcentageObjectif = Math.min((valeurFinale / objectif) * 100, 100);
    
    return {
      valeurFinale: Math.round(valeurFinale),
      capitalTotal: Math.round(capitalTotal),
      gain: Math.round(gain),
      pourcentageObjectif: Math.round(pourcentageObjectif),
      rendementMoyen: rendementAnnuel * 100
    };
  };

  const performance = calculatePerformance();

  // Génération des points pour le graphique
  const generateChartData = () => {
    const { horizon, capitalInitial, versementMensuel, profilInvestisseur } = simulatorData;
    const rendementAnnuel = profilsInvestisseurs[profilInvestisseur].taux;
    const points = [];
    const maxValue = Math.max(simulatorData.objectif, performance.valeurFinale);
    
    for (let year = 0; year <= horizon; year++) {
      const capitalCumule = capitalInitial + (versementMensuel * 12 * year);
      const valeurAvecInterets = year === 0 ? capitalInitial : 
        capitalInitial * Math.pow(1 + rendementAnnuel, year) + 
        versementMensuel * ((Math.pow(1 + rendementAnnuel/12, 12 * year) - 1) / (rendementAnnuel/12)) * 12;
      
      points.push({
        year,
        x: 40 + (year / horizon) * 320, // Position X sur le graphique (40 à 360)
        capitalY: 180 - (capitalCumule / maxValue) * 160, // Position Y capital investi
        valeurY: 180 - (valeurAvecInterets / maxValue) * 160, // Position Y valeur portefeuille
        objectifY: 180 - (simulatorData.objectif / maxValue) * 160 // Position Y objectif
      });
    }
    
    return points;
  };

  const chartData = generateChartData();

  // Fonction pour créer une courbe SVG lisse
  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return '';
    
    const smoothness = 0.2;
    let path = `M ${points[0].x},${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prevPoint = points[i - 1];
      const currentPoint = points[i];
      const nextPoint = points[i + 1];
      
      if (i === 1) {
        // Premier segment - courbe douce depuis le début
        const cp1x = prevPoint.x + (currentPoint.x - prevPoint.x) * smoothness;
        const cp1y = prevPoint.y + (currentPoint.y - prevPoint.y) * smoothness;
        const cp2x = currentPoint.x - (nextPoint ? (nextPoint.x - prevPoint.x) * smoothness : 0);
        const cp2y = currentPoint.y - (nextPoint ? (nextPoint.y - prevPoint.y) * smoothness : 0);
        
        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${currentPoint.x},${currentPoint.y}`;
      } else if (i === points.length - 1) {
        // Dernier segment
        const prevPrevPoint = points[i - 2];
        const cp1x = prevPoint.x + (currentPoint.x - prevPrevPoint.x) * smoothness;
        const cp1y = prevPoint.y + (currentPoint.y - prevPrevPoint.y) * smoothness;
        const cp2x = currentPoint.x - (currentPoint.x - prevPoint.x) * smoothness;
        const cp2y = currentPoint.y - (currentPoint.y - prevPoint.y) * smoothness;
        
        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${currentPoint.x},${currentPoint.y}`;
      } else {
        // Segments du milieu - courbes fluides
        const cp1x = prevPoint.x + (currentPoint.x - points[i - 2].x) * smoothness;
        const cp1y = prevPoint.y + (currentPoint.y - points[i - 2].y) * smoothness;
        const cp2x = currentPoint.x - (nextPoint.x - prevPoint.x) * smoothness;
        const cp2y = currentPoint.y - (nextPoint.y - prevPoint.y) * smoothness;
        
        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${currentPoint.x},${currentPoint.y}`;
      }
    }
    
    return path;
  };
  const carouselImages = [
    {
      src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Dashboard financier avancé",
      title: "Tableaux de bord experts",
      description: "Visualisation en temps réel"
    },
    {
      src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Analyse de données financières",
      title: "Analyse quantitative",
      description: "Modèles sophistiqués"
    },
    {
      src: "https://images.unsplash.com/photo-1590479773265-7464e5d48118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Stratégie d'investissement",
      title: "Stratégies personnalisées",
      description: "Allocation optimisée"
    },
    {
      src: "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Performance portfolio",
      title: "Performance supérieure",
      description: "Rendements optimisés"
    }
  ];

  // Auto-scroll du carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };
  return (
    <div className="hero-background">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex-between h-16">
            <div className="flex items-center space-x-4 flex-shrink-0">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-slate-700 via-slate-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg border border-gray-200">
                <div className="text-white font-bold text-lg md:text-xl tracking-wider">
                  S
                </div>
              </div>
              <div className="flex flex-col">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 tracking-tight">
                  SOeasi
                </h1>
                <div className="text-xs md:text-sm text-slate-600 font-medium tracking-widest uppercase">
                  Solutions Financières Ethiques
                </div>
              </div>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="#solutions" className="text-slate-600 hover:text-teal-600 font-medium transition-colors">Stratégies</a>
              <a href="#avantages" className="text-slate-600 hover:text-teal-600 font-medium transition-colors">Avantages</a>
              <a href="#tarifs" className="text-slate-600 hover:text-teal-600 font-medium transition-colors">Performance</a>
              <a href="#contact" className="text-slate-600 hover:text-teal-600 font-medium transition-colors">Expertise</a>
            </nav>
            <div className="flex items-center justify-end space-x-2 flex-shrink-0">
              {/* Boutons desktop */}
              <div className="hidden md:flex items-center space-x-4">
             
                <button 
                  onClick={onOpenMonCompte}
                  className="px-4 py-2 text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap bg-gradient-to-r from-cyan-800 to-slate-800 text-white shadow-lg hover:from-cyan-900 hover:to-slate-900"
                >
                  Espace client
                </button>
                <button 
                  onClick={onStartChat}
                  className="px-4 py-2 text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg hover:from-teal-700 hover:to-cyan-700"
                >
                  Nous contacter
                </button>
              </div>
              
              {/* Menu mobile avec boutons essentiels */}
              <div className="md:hidden flex items-center space-x-2">
            
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-lg text-slate-600 hover:text-teal-600 hover:bg-teal-50 transition-colors border border-slate-300"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Menu mobile */}
        <div className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-4 py-4 space-y-3">
            {/* Navigation links */}
            <div className="divide-y divide-gray-200">
              <a 
                href="#solutions" 
                className="block px-4 py-2 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Stratégies
              </a>
              <a 
                href="#avantages" 
                className="block px-4 py-2 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Avantages
              </a>
              <a 
                href="#tarifs" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Performance
              </a>
              <a 
                href="#contact" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50  transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Expertise
              </a>
            </div>
            
            {/* Boutons d'action mobile */}
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
              <button 
                onClick={() => {
                  onOpenMonCompte();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg text-white"
                style={{
                  backgroundColor: '#1e4586',
                  boxShadow: '0 0 10px rgba(30, 69, 134, 0.3)'
                }}
              >
                Espace client
              </button>
              <button 
                onClick={() => {
                  onStartChat();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full btn-primary"
              >
                Nous contacter
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Design Élégant et Moderne */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Contour lumineux fin et brillant */}
        <div className="absolute inset-8 rounded-3xl"
             style={{
               border: '1px solid transparent',
               backgroundImage: `
                 linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.8)),
                 linear-gradient(135deg, rgba(20, 184, 166, 0.8), rgba(6, 182, 212, 0.9), rgba(20, 184, 166, 0.8))
               `,
               backgroundOrigin: 'border-box',
               backgroundClip: 'content-box, border-box',
               backgroundSize: 'cover, cover',
               backgroundRepeat: 'no-repeat, no-repeat',
               boxShadow: `
                 0 0 20px rgba(20, 184, 166, 0.6),
                 0 0 40px rgba(6, 182, 212, 0.4),
                 0 0 60px rgba(20, 184, 166, 0.3),
                 inset 0 0 30px rgba(20, 184, 166, 0.1)
               `
             }}>
        </div>

        {/* Fond avec texture élégante */}
        <div className="absolute inset-8 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl"></div>
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '60px 60px'
            }}></div>
          </div>
        </div>

        {/* Éléments géométriques décoratifs avec étoiles filantes */}
        <div className="absolute inset-8 overflow-hidden rounded-2xl">

          {/* Points lumineux flottants */}
          <div className="absolute top-1/4 left-1/3 w-3 h-3 bg-teal-400/60 rounded-full animate-ping"></div>
          <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-cyan-400/60 rounded-full animate-ping delay-500"></div>
          <div className="absolute bottom-1/4 left-2/3 w-3 h-3 bg-teal-300/60 rounded-full animate-ping delay-1000"></div>

          {/* Lignes statiques */}
          <div className="absolute top-1/3 right-1/4 w-1 h-16 bg-gradient-to-b from-teal-400/40 to-transparent transform rotate-45 shadow-sm shadow-teal-400/20"></div>
          <div className="absolute bottom-1/3 left-1/4 w-1 h-14 bg-gradient-to-b from-cyan-400/40 to-transparent transform -rotate-45 shadow-sm shadow-cyan-400/20"></div>
        </div>

        {/* Courbe décorative élégante */}
        <div className="absolute bottom-12 right-12 w-1/2 h-1/2 overflow-hidden rounded-br-2xl">
          <svg className="absolute bottom-0 right-0 w-full h-full" viewBox="0 0 400 400" fill="none">
            <path
              d="M400 400C400 200 300 100 200 100C100 100 0 200 0 400H400Z"
              fill="url(#heroGradient)"
              className="opacity-10"
            />
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 0.3}} />
                <stop offset="100%" style={{stopColor: '#06b6d4', stopOpacity: 0.1}} />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center max-w-5xl mx-auto">
            
            {/* Badge élégant */}
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 text-white/90 px-6 py-3 rounded-full text-sm font-medium mb-8 shadow-2xl">
              <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></div>
              <span>SOeasi la finance éthique</span>
              <span className="text-teal-300 font-semibold">accessible à tous</span>
            </div>

            {/* Titre principal avec typographie élégante */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] text-white mb-8">
              L'Alliance de la{' '}
              <span className="font-normal">simplicité</span>
              <br />
              et{' '}
              <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent font-normal">
                de l'éthique
              </span>
            </h1>

            {/* Sous-titre élégant */}
            <p className="text-xl md:text-2xl text-white/80 mb-12 font-light leading-relaxed max-w-3xl mx-auto">
              pour une expérience utilisateur sans accroc
            </p>

            {/* CTA élégant */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
              <button
                onClick={onStartProfilRisque}
                className="group relative bg-white text-slate-800 px-10 py-5 rounded-2xl font-semibold text-lg hover:bg-gray-50 transition-all duration-300 flex items-center space-x-3 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                <Calculator className="w-6 h-6" />
                <span>Découvrir nos solutions</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button
                onClick={onStartChat}
                className="group border-2 border-white/30 text-white px-10 py-5 rounded-2xl font-medium text-lg hover:border-white/50 hover:bg-white/10 transition-all duration-300 flex items-center space-x-3 backdrop-blur-md"
              >
                <span>Mon assistant IA</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Indicateurs de performance discrets */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-white/60">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm">+15K investisseurs</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="text-sm">100% conformité</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-300 rounded-full"></div>
                <span className="text-sm">Démarrage 3min</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Nouveau Simulateur Chic et Fluide */}
      <section className="simulation-patrimoine-section">
        {/* Éléments décoratifs subtils */}
        <div className="simulation-decoration-1"></div>
        <div className="simulation-decoration-2"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header élégant */}
          <div className="text-center mb-16">
            <div className="simulation-badge">
              <div className="simulation-badge-pulse"></div>
              <span>Simulateur Intelligent</span>
            </div>

            <h2 className="simulation-title">
              Calculez votre{' '}
              <span className="simulation-title-gradient">
                potentiel d'investissement
              </span>
            </h2>

            <p className="simulation-description">
              Découvrez en temps réel comment optimiser votre patrimoine avec nos solutions d'investissement personnalisées
            </p>
          </div>

          {/* Simulateur principal - design moderne */}
          <div className="simulation-main-container">
            <div className="simulation-card">

              {/* Header du simulateur */}
              <div className="simulation-header">
                <div className="simulation-header-layout">
                  <h3 className="simulation-header-title">Simulation Patrimoine</h3>
                  <div className="simulation-status">
                    <div className="simulation-status-dot"></div>
                    <span className="simulation-status-text">En temps réel</span>
                  </div>
                </div>
              </div>

              {/* Corps du simulateur */}
              <div className="simulation-body">
                <div className="simulation-grid">

                  {/* Panneau de contrôles - Gauche */}
                  <div className="simulation-controls">

                    {/* Capital initial */}
                    <div className="simulation-control-group">
                      <div className="simulation-control-header">
                        <label className="simulation-control-label">Capital initial</label>
                        <div className="simulation-control-display">
                          <div className="simulation-control-value">{simulatorData.capitalInitial.toLocaleString()}€</div>
                          <div className="simulation-control-unit">Montant de départ</div>
                        </div>
                      </div>
                      <div className="simulation-slider-container">
                        <input
                          type="range"
                          min="1000"
                          max="100000"
                          step="1000"
                          value={simulatorData.capitalInitial}
                          onChange={(e) => setSimulatorData({...simulatorData, capitalInitial: parseInt(e.target.value)})}
                          className="simulation-slider"
                          style={{
                            background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${simulatorData.capitalInitial / 1000}%, #E5E7EB ${simulatorData.capitalInitial / 1000}%, #E5E7EB 100%)`
                          }}
                        />
                        <div className="simulation-slider-labels">
                          <span>1k€</span>
                          <span>100k€</span>
                        </div>
                      </div>
                    </div>

                    {/* Versement mensuel */}
                    <div className="simulation-control-group">
                      <div className="simulation-control-header">
                        <label className="simulation-control-label">Versement mensuel</label>
                        <div className="simulation-control-display">
                          <div className="simulation-control-value">{simulatorData.versementMensuel}€</div>
                          <div className="simulation-control-unit">Par mois</div>
                        </div>
                      </div>
                      <div className="simulation-slider-container">
                        <input
                          type="range"
                          min="50"
                          max="2000"
                          step="50"
                          value={simulatorData.versementMensuel}
                          onChange={(e) => setSimulatorData({...simulatorData, versementMensuel: parseInt(e.target.value)})}
                          className="simulation-slider"
                          style={{
                            background: `linear-gradient(to right, #10B981 0%, #10B981 ${(simulatorData.versementMensuel - 50) / 1950 * 100}%, #E5E7EB ${(simulatorData.versementMensuel - 50) / 1950 * 100}%, #E5E7EB 100%)`
                          }}
                        />
                        <div className="simulation-slider-labels">
                          <span>50€</span>
                          <span>2000€</span>
                        </div>
                      </div>
                    </div>

                    {/* Horizon de placement */}
                    <div className="simulation-control-group">
                      <div className="simulation-control-header">
                        <label className="simulation-control-label">Horizon de placement</label>
                        <div className="simulation-control-display">
                          <div className="simulation-control-value">{simulatorData.horizon} ans</div>
                          <div className="simulation-control-unit">Durée d'investissement</div>
                        </div>
                      </div>
                      <div className="simulation-slider-container">
                        <input
                          type="range"
                          min="1"
                          max="30"
                          value={simulatorData.horizon}
                          onChange={(e) => setSimulatorData({...simulatorData, horizon: parseInt(e.target.value)})}
                          className="simulation-slider"
                          style={{
                            background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${(simulatorData.horizon - 1) / 29 * 100}%, #E5E7EB ${(simulatorData.horizon - 1) / 29 * 100}%, #E5E7EB 100%)`
                          }}
                        />
                        <div className="simulation-slider-labels">
                          <span>1 an</span>
                          <span>30 ans</span>
                        </div>
                      </div>
                    </div>

                    {/* Métriques détaillées - Déplacées ici */}
                    <div className="simulation-metrics">
                      <div className="simulation-metric-card simulation-metric-blue">
                        <div className="simulation-metric-content">
                          <div className="simulation-metric-value">
                            {((simulatorData.capitalInitial + simulatorData.versementMensuel * 12 * simulatorData.horizon) / 1000).toFixed(0)}k€
                          </div>
                          <div className="simulation-metric-label">Capital investi</div>
                        </div>
                      </div>

                      <div className="simulation-metric-card simulation-metric-purple">
                        <div className="simulation-metric-content">
                          <div className="simulation-metric-value">
                            {((performance.valeurFinale / (simulatorData.capitalInitial + simulatorData.versementMensuel * 12 * simulatorData.horizon) - 1) * 100).toFixed(0)}%
                          </div>
                          <div className="simulation-metric-label">Performance totale</div>
                        </div>
                      </div>
                    </div>

                    {/* Call-to-action - Déplacé ici */}
                    <div className="simulation-cta">
                      <h4 className="simulation-cta-title">Prêt à commencer ?</h4>
                      <p className="simulation-cta-description">Obtenez votre stratégie personnalisée en 5 minutes</p>

                      <button
                        onClick={onStartProfilRisque}
                        className="simulation-cta-button"
                      >
                        Démarrer mon analyse gratuite
                      </button>
                    </div>
                  </div>

                  {/* Résultats - Droite */}
                  <div className="simulation-results">

                    {/* Résultat principal - Taille réduite */}
                    <div className="simulation-result-main">
                      <div className="simulation-result-content">
                        <div className="simulation-result-value">
                          {(performance.valeurFinale / 1000).toFixed(0)}k€
                        </div>
                        <div className="simulation-result-label">Valeur totale estimée</div>

                        <div className="simulation-result-details">
                          <div className="simulation-result-detail">
                            <div className="simulation-result-detail-value">
                              +{((performance.valeurFinale - (simulatorData.capitalInitial + simulatorData.versementMensuel * 12 * simulatorData.horizon)) / 1000).toFixed(0)}k€
                            </div>
                            <div className="simulation-result-detail-label">Plus-value</div>
                          </div>
                          <div className="simulation-result-detail">
                            <div className="simulation-result-detail-value">{performance.rendementMoyen}%</div>
                            <div className="simulation-result-detail-label">Rendement annuel</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Courbe d'évolution des rendements - Taille augmentée */}
                    <div className="simulation-chart-container">
                      <h4 className="simulation-chart-title">Évolution de votre patrimoine</h4>

                      <div className="simulation-chart">
                        <svg className="simulation-svg-full" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid meet">
                          <defs>
                            {/* Gradient pour la zone sous la courbe */}
                            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" style={{ stopColor: '#10B981', stopOpacity: 0.3 }} />
                              <stop offset="100%" style={{ stopColor: '#10B981', stopOpacity: 0.05 }} />
                            </linearGradient>

                            {/* Gradient pour la courbe */}
                            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#3B82F6' }} />
                              <stop offset="50%" style={{ stopColor: '#10B981' }} />
                              <stop offset="100%" style={{ stopColor: '#06B6D4' }} />
                            </linearGradient>
                          </defs>

                          {/* Grille de fond */}
                          <g className="simulation-grid-opacity">
                            {[1, 2, 3, 4].map((i) => (
                              <line
                                key={`horizontal-${i}`}
                                x1="40"
                                y1={40 + (i * 40)}
                                x2="360"
                                y2={40 + (i * 40)}
                                stroke="#E5E7EB"
                                strokeWidth="1"
                              />
                            ))}
                            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                              <line
                                key={`vertical-${i}`}
                                x1={40 + (i * 45)}
                                y1="40"
                                x2={40 + (i * 45)}
                                y2="180"
                                stroke="#E5E7EB"
                                strokeWidth="1"
                              />
                            ))}
                          </g>

                          {/* Axes */}
                          <line x1="40" y1="40" x2="40" y2="180" stroke="#6B7280" strokeWidth="2" />
                          <line x1="40" y1="180" x2="360" y2="180" stroke="#6B7280" strokeWidth="2" />

                          {/* Génération des points de données */}
                          {(() => {
                            const points = [];
                            const capitalPoints = [];
                            const years = Math.min(simulatorData.horizon, 8); // Limiter à 8 points pour la lisibilité

                            for (let i = 0; i <= years; i++) {
                              const year = (simulatorData.horizon / years) * i;
                              const x = 40 + (i * (320 / years));

                              // Calcul de la valeur investie
                              const capitalInvesti = simulatorData.capitalInitial + (simulatorData.versementMensuel * 12 * year);

                              // Calcul de la valeur avec rendements
                              const taux = profilsInvestisseurs[simulatorData.profilInvestisseur].taux;
                              const valeurAvecRendements = simulatorData.capitalInitial * Math.pow(1 + taux, year) +
                                simulatorData.versementMensuel * 12 * ((Math.pow(1 + taux, year) - 1) / taux);

                              // Normalisation pour l'affichage (échelle de 40 à 180)
                              const maxValue = Math.max(performance.valeurFinale, simulatorData.capitalInitial + simulatorData.versementMensuel * 12 * simulatorData.horizon);
                              const yPortfolio = 180 - ((valeurAvecRendements / maxValue) * 140);
                              const yCapital = 180 - ((capitalInvesti / maxValue) * 140);

                              points.push({ x, y: yPortfolio, value: valeurAvecRendements, year: Math.round(year) });
                              capitalPoints.push({ x, y: yCapital, value: capitalInvesti, year: Math.round(year) });
                            }

                            // Création du path pour la zone sous la courbe
                            const areaPath = `M 40,180 L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},180 Z`;

                            // Création du path pour la courbe des rendements
                            const linePath = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;

                            // Création du path pour la courbe du capital
                            const capitalPath = `M ${capitalPoints.map(p => `${p.x},${p.y}`).join(' L ')}`;

                            return (
                              <g>
                                {/* Zone sous la courbe */}
                                <path
                                  d={areaPath}
                                  fill="url(#areaGradient)"
                                />

                                {/* Courbe du capital investi */}
                                <path
                                  d={capitalPath}
                                  fill="none"
                                  stroke="#94A3B8"
                                  strokeWidth="2"
                                  strokeDasharray="5,5"
                                  opacity="0.7"
                                />

                                {/* Courbe des rendements */}
                                <path
                                  d={linePath}
                                  fill="none"
                                  stroke="url(#lineGradient)"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />

                                {/* Points sur la courbe */}
                                {points.map((point, index) => (
                                  <g key={index}>
                                    <circle
                                      cx={point.x}
                                      cy={point.y}
                                      r="4"
                                      fill="#10B981"
                                      stroke="white"
                                      strokeWidth="2"
                                      className="simulation-hover-scale"
                                    />
                                    {/* Tooltip au survol */}
                                    <g className="simulation-tooltip-hidden simulation-tooltip-transition hover:opacity-100">
                                      <rect
                                        x={point.x - 30}
                                        y={point.y - 45}
                                        width="60"
                                        height="35"
                                        fill="#1F2937"
                                        rx="4"
                                      />
                                      <text
                                        x={point.x}
                                        y={point.y - 28}
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="10"
                                        fontWeight="600"
                                      >
                                        Année {point.year}
                                      </text>
                                      <text
                                        x={point.x}
                                        y={point.y - 16}
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="10"
                                        fontWeight="600"
                                      >
                                        {(point.value / 1000).toFixed(0)}k€
                                      </text>
                                    </g>
                                  </g>
                                ))}

                                {/* Labels des années sur l'axe X */}
                                {points.map((point, index) => (
                                  <text
                                    key={`year-${index}`}
                                    x={point.x}
                                    y="195"
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="#6B7280"
                                    fontWeight="500"
                                  >
                                    {point.year}
                                  </text>
                                ))}

                                {/* Labels des valeurs sur l'axe Y */}
                                {(() => {
                                  const maxValue = Math.max(performance.valeurFinale, simulatorData.capitalInitial + simulatorData.versementMensuel * 12 * simulatorData.horizon);
                                  const intervals = [0, 0.25, 0.5, 0.75, 1].map(ratio => ({
                                    value: maxValue * ratio,
                                    y: 180 - (ratio * 140)
                                  }));

                                  return intervals.map((interval, index) => (
                                    <text
                                      key={`value-${index}`}
                                      x="35"
                                      y={interval.y + 3}
                                      textAnchor="end"
                                      fontSize="10"
                                      fill="#6B7280"
                                      fontWeight="500"
                                    >
                                      {(interval.value / 1000).toFixed(0)}k
                                    </text>
                                  ));
                                })()}
                              </g>
                            );
                          })()}

                          {/* Légende */}
                          <g transform="translate(50, 25)">
                            <g>
                              <line x1="0" y1="0" x2="15" y2="0" stroke="url(#lineGradient)" strokeWidth="3" />
                              <text x="20" y="4" fontSize="12" fill="#374151" fontWeight="500">Patrimoine avec rendements</text>
                            </g>
                            <g transform="translate(0, 15)">
                              <line x1="0" y1="0" x2="15" y2="0" stroke="#94A3B8" strokeWidth="2" strokeDasharray="5,5" />
                              <text x="20" y="4" fontSize="12" fill="#374151" fontWeight="500">Capital investi</text>
                            </g>
                          </g>

                          {/* Labels des axes */}
                          <text x="200" y="195" textAnchor="middle" fontSize="12" fill="#6B7280">Années</text>
                          <text x="25" y="115" textAnchor="middle" fontSize="12" fill="#6B7280" transform="rotate(-90, 25, 115)">Valeur (€)</text>
                        </svg>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Choisissez entre SCPI, GFI ou Sukuk */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-2 animate-pulse"></div>
                  Produits d'Investissement Conformes
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  Choisissez entre{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #0d9488 0%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent'
                  }}>
                    SCPI, GFI ou Sukuk
                  </span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Trois solutions d'investissement adaptées à tous les profils. Comparez les rendements, les risques et choisissez votre allocation idéale.
                </p>
              </div>

              <div className="pt-4">
                <button
                  onClick={onStartSimulateurProduits}
                  className="group bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <PieChart className="w-6 h-6" />
                  <span>Comparer les produits</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Photos des 3 produits avec rendements */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* SCPI */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="Laboratoire SCPI - Recherche et innovation"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="overlay-gradient"></div>
                  <div className="absolute-top-left">
                    <span className="badge-emerald">
                      SCPI
                    </span>
                  </div>
                  <div className="absolute-bottom-right">
                    <div className="stats-card">
                      <div className="stats-value">4.5%</div>
                      <div className="stats-label">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Immobilier Locatif</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Investissement dans l'immobilier commercial et résidentiel avec revenus locatifs garantis.
                  </p>
                  <div className="flex-between text-sm">
                    <span className="text-gray-500">Risque: Modéré</span>
                    <span className="text-emerald-600 font-semibold">↗ +0.3%</span>
                  </div>
                </div>
              </div>

              {/* GFI */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="GFI - Forêts et investissement écologique"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="overlay-gradient"></div>
                  <div className="absolute-top-left">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      GFI
                    </span>
                  </div>
                  <div className="absolute-bottom-right">
                    <div className="stats-card">
                      <div className="text-blue-600 text-2xl font-bold">5.5%</div>
                      <div className="stats-label">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Fonds de Placement</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Portefeuille diversifié d'actifs financiers gérés par des experts avec performance optimisée.
                  </p>
                  <div className="flex-between text-sm">
                    <span className="text-gray-500">Risque: Élevé</span>
                    <span className="text-blue-600 font-semibold">↗ +0.8%</span>
                  </div>
                </div>
              </div>

              {/* Sukuk */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="Sukuk - Maison moderne et résidentiel"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="overlay-gradient"></div>
                  <div className="absolute-top-left">
                    <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Sukuk
                    </span>
                  </div>
                  <div className="absolute-bottom-right">
                    <div className="stats-card">
                      <div className="text-amber-600 text-2xl font-bold">7.0%</div>
                      <div className="stats-label">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Finance Islamique</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Investissement conforme à la Sharia, basé sur des actifs tangibles et des principes éthiques.
                  </p>
                  <div className="flex-between text-sm">
                    <span className="text-gray-500">Risque: Faible</span>
                    <span className="text-amber-600 font-semibold">→ Stable</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Pricing Section - Tarification Transparente */}
      <section className="py-20 relative overflow-hidden">
        {/* Background similaire aux autres sections */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header avec style simulator */}
          <div className="text-center mx-auto max-w-4xl mb-16">
            {/* Badge minimaliste */}
            <div className="inline-flex items-center bg-white shadow-lg border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-xs font-medium mb-8 uppercase tracking-wider">
              <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
              <span>Tarification Transparente</span>
            </div>

            {/* Titre avec style simulator */}
            <h2 className="simulator-title mb-8">
              <span className="simulator-title-primary">Choisissez votre</span>
              <br />
              <span className="simulator-title-gradient">parcours</span>
            </h2>

            {/* Description avec style simulator */}
            <p className="simulator-description mb-12">
              Deux options adaptées à vos besoins : autonomie complète ou accompagnement personnalisé par nos experts.
            </p>

            {/* Ligne décorative centrée */}
            <div className="w-24 h-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto mb-16"></div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {/* Parcours en Autonomie */}
            <div className="group">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 hover:shadow-md transition-all duration-300 relative overflow-hidden">
                {/* Badge "Offert" */}
                <div className="absolute top-4 right-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  OFFERT
                </div>

                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Parcours en Autonomie</h3>
                  <div className="w-12 h-0.5 bg-teal-500 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm leading-relaxed">Accédez gratuitement à tous nos outils et simulateurs</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-3xl font-bold text-gray-900">Offert</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Simulateurs patrimoniaux complets
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Analyses de performance
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Rapports détaillés PDF
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Support communautaire
                  </div>
                </div>

                <button
                  onClick={onStartSimulator}
                  className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium text-sm hover:bg-gray-800 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Commencer gratuitement</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Parcours Accompagnement Expert */}
            <div className="group">
              <div className="bg-white rounded-lg shadow-sm border-2 border-teal-200 p-8 hover:shadow-md transition-all duration-300 relative overflow-hidden">
                {/* Badge "Recommandé" */}
                <div className="absolute top-4 right-4 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-medium">
                  RECOMMANDÉ
                </div>

                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Accompagnement Expert</h3>
                  <div className="w-12 h-0.5 bg-teal-500 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm leading-relaxed">Suivi personnalisé par nos conseillers patrimoniaux</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-3xl font-bold text-gray-900">Sur mesure</span>
                  </div>
                  <p className="text-gray-500 text-sm">Tarification adaptée à votre situation</p>
                </div>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Tout du parcours autonomie
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Conseiller patrimonial dédié
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Stratégie personnalisée
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Suivi trimestriel
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    Support prioritaire 24/7
                  </div>
                </div>

                <button className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg font-medium text-sm hover:bg-teal-700 transition-all duration-300 flex items-center justify-center space-x-2">
                  <span>Prendre rendez-vous</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Stratégie Financière Sur Mesure Section - Design Chic */}
      <section id="solutions" className="py-20 relative overflow-hidden">
        {/* Fond avec texture élégante */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100"></div>
        
        {/* Bande colorée élégante sur le côté */}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header centré avec style simulator */}
          <div className="text-center mx-auto max-w-4xl">
            {/* Badge minimaliste */}
            <div className="inline-flex items-center bg-white shadow-lg border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-xs font-medium mb-8 uppercase tracking-wider">
              <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
              <span>Expertise Financière</span>
            </div>
            
            {/* Titre avec style simulator */}
            <h2 className="simulator-title mb-8">
              <span className="simulator-title-primary">Stratégie financière</span>
              <br />
              <span className="simulator-title-gradient">sur mesure</span>
            </h2>
            
            {/* Description avec style simulator */}
            <p className="simulator-description mb-12">
              Une approche personnalisée qui allie expertise financière et innovation, 
              conçue pour optimiser votre patrimoine selon vos objectifs uniques.
            </p>
            
            {/* Ligne décorative centrée */}
            <div className="w-24 h-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto mb-16"></div>
          </div>


          {/* Contenu structuré et épuré */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Pilier 1: Analyse */}
            <div className="group">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 hover:shadow-md transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Analyse patrimoniale</h3>
                  <div className="w-12 h-0.5 bg-teal-500 mx-auto"></div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 text-center">
                  Évaluation complète de votre situation financière et identification des opportunités d'optimisation.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Audit patrimonial complet</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Analyse fiscale avancée</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Évaluation des risques</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pilier 2: Stratégie */}
            <div className="group">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 hover:shadow-md transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Allocation optimisée</h3>
                  <div className="w-12 h-0.5 bg-teal-500 mx-auto"></div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 text-center">
                  Stratégie d'allocation personnalisée combinant SCPI, GFI et Sukuk selon votre profil de risque.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Diversification multi-actifs</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Rendements décorrélés</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Protection contre l'inflation</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pilier 3: Suivi */}
            <div className="group">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 hover:shadow-md transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Suivi performance</h3>
                  <div className="w-12 h-0.5 bg-teal-500 mx-auto"></div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 text-center">
                  Monitoring continu et ajustements stratégiques pour optimiser vos performances.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Reporting mensuel</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Rééquilibrage automatique</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                    <span>Optimisation continue</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

            {/* Carousel d'images moderne */}
            <div className="relative mt-16">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 overflow-hidden">
                <div className="text-center p-6 pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Excellence SOeasi Expert</h4>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full mx-auto"></div>
                </div>
                
                {/* Container du carousel */}
                <div className="relative h-80 overflow-hidden">
                  {carouselImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                        index === currentImageIndex ? 'translate-x-0' : 
                        index < currentImageIndex ? '-translate-x-full' : 'translate-x-full'
                      }`}
                    >
                      <div className="relative h-full">
                        <img 
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        
                        {/* Overlay de texte */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h5 className="text-lg font-bold mb-1">{image.title}</h5>
                          <p className="text-sm text-white/90">{image.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Boutons de navigation */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 group"
                  >
                    <ChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 group"
                  >
                    <ChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
                
                {/* Indicateurs */}
                <div className="flex justify-center gap-2 p-6 pt-4">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-gradient-to-r from-blue-500 to-emerald-500 scale-125' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="px-6 pb-6">
                  <div className="p-4 bg-gradient-to-r from-blue-50/50 to-emerald-50/50 rounded-2xl border border-blue-200/30">
                    <p className="text-xs text-gray-700 text-center leading-relaxed">
                      <span className="font-semibold">Découvrez</span> nos outils experts et tableaux de bord avancés 
                      conçus pour optimiser vos investissements SCPI, GFI et Sukuk.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          {/* Call to Action moderne */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-10 text-white relative overflow-hidden">
              {/* Décoration de fond */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold mb-6">Prêt pour une stratégie personnalisée ?</h3>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                  Nos experts analysent votre situation et conçoivent une stratégie d'investissement 
                  parfaitement adaptée à vos objectifs patrimoniaux avec nos 3 produits privilégiés.
                </p>
                <button
                  onClick={onStartSimulator}
                  className="bg-white/95 text-slate-800 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white transition-all duration-300 inline-flex items-center space-x-3 shadow-2xl hover:shadow-xl hover:scale-105 transform"
                >
                  <TrendingUp className="w-6 h-6" />
                  <span>Commencer mon analyse</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Avantages Section Modernisée */}
      <section id="avantages" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 left-10 w-32 h-32 border border-teal-500/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-teal-500/10 rounded-full blur-xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-white/80 backdrop-blur-sm shadow-lg border border-gray-200/50 text-slate-700 px-6 py-3 rounded-full text-sm font-medium mb-8 uppercase tracking-wider">
              <CheckCircle className="w-4 h-4 mr-2 text-teal-600" />
              <span>Nos Avantages</span>
            </div>
            
            <h2 className="simulator-title mb-6">
              <span className="simulator-title-primary">Pourquoi choisir</span>
              <br />
              <span className="simulator-title-gradient">SOeasi   ?</span>
            </h2>
            
            <p className="simulator-description max-w-3xl mx-auto">
              Découvrez les atouts qui font de SOeasi   le partenaire privilégié 
              des entreprises pour leur gestion patrimoniale et leurs investissements.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                {/* Expertise reconnue */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start space-x-5">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                        Expertise reconnue
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        Plus de 10 ans d'expérience dans la gestion patrimoniale d'entreprise avec 
                        une équipe d'experts certifiés AMF et une track record prouvée.
                      </p>
                      <div className="mt-3 flex items-center text-sm text-blue-600 font-medium">
                        <span>Certification AMF</span>
                        <div className="w-2 h-2 bg-blue-500 rounded-full mx-2"></div>
                        <span>+500 clients satisfaits</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Solutions sur mesure */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start space-x-5">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Globe className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors">
                        Solutions sur mesure
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        Chaque entreprise est unique. Nous analysons votre secteur, vos contraintes 
                        et concevons des stratégies parfaitement adaptées à votre contexte.
                      </p>
                      <div className="mt-3 flex items-center text-sm text-indigo-600 font-medium">
                        <span>Analyse personnalisée</span>
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mx-2"></div>
                        <span>Accompagnement dédié</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sécurité maximale */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start space-x-5">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors">
                        Sécurité maximale
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        Conformité réglementaire totale, transparence sur les risques et reporting 
                        détaillé pour une sérénité absolue dans vos investissements.
                      </p>
                      <div className="mt-3 flex items-center text-sm text-teal-600 font-medium">
                        <span>Conformité AMF</span>
                        <div className="w-2 h-2 bg-teal-500 rounded-full mx-2"></div>
                        <span>Reporting transparent</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              {/* Image avec overlay moderne */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-teal-500/20 rounded-3xl blur-3xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-4 border border-white/20 shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80"
                    alt="Dashboard   SOeasi"
                    className="w-full h-96 object-cover rounded-2xl shadow-xl group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Overlay avec stats */}
                  <div className="absolute bottom-8 left-8 right-8">
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-2xl">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-slate-900">500+</div>
                          <div className="text-xs text-slate-600">Entreprises</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-slate-900">€2.5M</div>
                          <div className="text-xs text-slate-600">Actifs gérés</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-slate-900">10+</div>
                          <div className="text-xs text-slate-600">Années</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Stratégie Financière Complète - Version Expert - HIDDEN */}
      {false && <section className="py-12 md:py-20 relative overflow-hidden" style={{backgroundColor: '#082F49'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-flex items-center bg-blue-500/10 text-blue-400 px-6 py-3 rounded-full text-sm font-medium mb-6 border border-blue-500/20">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Excellence pour investisseurs avertis</span>
            </div>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              Stratégies d'investissement{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
                complètes
              </span>
            </h2>
            <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              SOeasi Expert déploie une approche institutionnelle qui combine analyse quantitative, 
              diversification alternative, gestion des risques avancée et optimisation fiscale 
              pour maximiser la performance ajustée du risque de votre portefeuille.
            </p>
          </div>

          {/* Network Visualization - Version Expert */}
          <div className="relative flex items-center justify-center min-h-[400px] sm:min-h-[500px] md:min-h-[700px] lg:min-h-[800px] max-w-6xl mx-auto mt-6 md:mt-12">
            
            {/* Central Hub - Portfolio Management */}
            <div className="relative z-20 animate-node-center">
              <div className="w-24 h-24 md:w-32 lg:w-40 h-24 md:h-32 lg:h-40 bg-slate-700 rounded-full border-2 md:border-4 border-blue-400 flex items-center justify-center shadow-2xl relative">
                {/* Cercles concentriques décoratifs */}
                <div className="absolute inset-0 rounded-full border-2 border-blue-400/20 animate-pulse"></div>
                <div className="absolute inset-2 rounded-full border border-blue-400/10"></div>
                
                <div className="text-center relative z-10">
                  <div className="w-8 h-8 md:w-10 lg:w-12 h-8 md:h-10 lg:h-12 mx-auto mb-1 md:mb-2 bg-blue-400 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 md:w-5 lg:w-6 h-4 md:h-5 lg:h-6 text-slate-800" />
                  </div>
                  <h3 className="text-white font-bold text-xs md:text-sm lg:text-base">Portfolio</h3>
                  <h3 className="text-blue-400 font-bold text-xs md:text-xs">Expert</h3>
                </div>
              </div>
            </div>

            {/* Orbit Container avec rotation */}
            <div className="absolute inset-0 orbit-container">
              
              {/* Investissements Alternatifs - Position 0° (haut) */}
              <div className="node-position-1 animate-node-1">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Building className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Alternatifs</h4>
                    <p className="text-gray-300 text-xs mt-1">Private Equity, Hedge Funds, REITs</p>
                  </div>
                </div>
              </div>

              {/* Analyse Quantitative - Position 60° (haut droite) */}
              <div className="node-position-2 animate-node-2">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Analyse</h4>
                    <h4 className="text-white font-bold text-sm">Quantitative</h4>
                    <p className="text-gray-300 text-xs mt-1">Modèles algorithmiques avancés</p>
                  </div>
                </div>
              </div>

              {/* Gestion des Risques - Position 120° (bas droite) */}
              <div className="node-position-3 animate-node-3">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Gestion des</h4>
                    <h4 className="text-white font-bold text-sm">Risques</h4>
                    <p className="text-gray-300 text-xs mt-1">VaR, tests de stress, couverture</p>
                  </div>
                </div>
              </div>

              {/* Structuration Fiscale - Position 180° (bas) */}
              <div className="node-position-4 animate-node-4">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Structuration</h4>
                    <h4 className="text-white font-bold text-sm">Fiscale</h4>
                    <p className="text-gray-300 text-xs mt-1">Optimisation juridique et fiscale</p>
                  </div>
                </div>
              </div>

              {/* Trading Algorithmique - Position 240° (bas gauche) */}
              <div className="node-position-5 animate-node-5">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Trading</h4>
                    <h4 className="text-white font-bold text-sm">Algorithmique</h4>
                    <p className="text-gray-300 text-xs mt-1">Exécution automatisée optimisée</p>
                  </div>
                </div>
              </div>

              {/* ESG & Impact - Position 300° (haut gauche) */}
              <div className="node-position-6 animate-node-6">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Globe className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">ESG &</h4>
                    <h4 className="text-white font-bold text-sm">Impact</h4>
                    <p className="text-gray-300 text-xs mt-1">Investissement responsable</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Lignes de connexion animées */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
              <defs>
                <radialGradient id="connectionGradientExpert" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
                </radialGradient>
              </defs>
              
              {/* Lignes pointillées du centre vers chaque nœud */}
              <g stroke="url(#connectionGradientExpert)" strokeWidth="2" strokeDasharray="4,4" fill="none">
                <line x1="50%" y1="50%" x2="50%" y2="15%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="0.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="50%" y2="85%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2.5s" repeatCount="indefinite"/>
                </line>
              </g>
            </svg>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-8 md:mt-16">
            <button
              onClick={onStartSimulator}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 mx-auto"
            >
              <span>Analyser mon portefeuille</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>}

      {/* Pricing Section -   - HIDDEN TEMPORARILY */}
      {false && (
        <section id="tarifs" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Nos offres  
              </h2>
              <p className="text-xl text-gray-600">
                Des tarifs transparents adaptés à la taille de votre entreprise
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Plan Starter */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Starter</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur devis</div>
                <p className="text-gray-600 mb-6">Pour les PME (&lt; 50 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Gestion de base</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Support par email</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting mensuel</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  Demander un devis
                </button>
              </div>

              {/* Plan Professional */}
              <div className="bg-gradient-to-t from-blue-50 to-white rounded-2xl p-8 shadow-xl border-2 border-blue-200 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Le plus populaire
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Professional</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur devis</div>
                <p className="text-gray-600 mb-6">Pour les entreprises (50-250 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Gestion avancée</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Conseiller dédié</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting hebdomadaire</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Formation équipes</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  Demander un devis
                </button>
              </div>

              {/* Plan Enterprise */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Enterprise</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur mesure</div>
                <p className="text-gray-600 mb-6">Pour les grandes entreprises (&gt; 250 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Solution sur mesure</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Équipe dédiée</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting temps réel</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>API intégration</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  Nous contacter
                </button>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Footer   */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-600 via-slate-700 to-gray-800 rounded-lg flex items-center justify-center shadow-lg border border-gray-700">
                  <div className="text-white font-bold text-xl tracking-wider">
                    S
                  </div>
                </div>
                <div className="flex flex-col">
                  <h3 className="text-2xl font-bold text-white">
                    SOeasi
                  </h3>
                  <div className="text-xs text-gray-400 font-medium tracking-widest uppercase">
                    Solutions Financières Ethiques
                  </div>
                </div>
              </div>
              <p className="text-gray-400">
                Solutions patrimoniales professionnelles pour entreprises et dirigeants.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Gestion d'entreprise</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Épargne salariale</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Conseils dirigeants</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Optimisation fiscale</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Formations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support technique</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li> @soeasi.fr</li>
                <li>+33 1 XX XX XX XX</li>
                <li>Disponible 24h/24 - 7j/7</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SOeasi  . Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Option2;